import type { OnInit } from '@angular/core';
import { DestroyRef, Directive, inject, input, output } from '@angular/core';

import type { FormTransaction } from '@sites/entity-signal-form';

/**
 * Base for the settings sub-panels of the form and payment-form widgets. A panel edits the shared widget form live
 * inside one transaction frame it opens itself: Save (button / Enter) commits it, Cancel (button / back arrow / Escape)
 * rolls it back, and a panel destroyed any other way (panel ×, another element selected, breadcrumbs) rolls it back
 * too — a frame that outlived its panel would keep the form from ever emitting again. `saveEvent` / `cancelEvent` only
 * tell the parent control to switch back to the main panel.
 *
 * Subclasses that implement `ngOnInit` must call `super.ngOnInit()` first, so their own writes land in the frame.
 */
@Directive({
    host: {
        '(document:keydown.enter)': 'onSave()',
        '(document:keydown.escape)': '$event.stopPropagation(); onReset()',
    },
})
export abstract class FormSettingsPanel<F extends FormTransaction<unknown>> implements OnInit {
    public readonly form = input.required<F>();

    public readonly saveEvent = output<void>();
    public readonly cancelEvent = output<void>();

    protected readonly destroyRef = inject(DestroyRef);

    private isTransactionOpen = false;

    public ngOnInit(): void {
        this.form().beginTransaction();
        this.isTransactionOpen = true;
        this.destroyRef.onDestroy(() => this.closeTransaction(false));
    }

    /** Overridden by panels that can block Save; also feeds `web-settings-footer`'s `isSaveDisabled`. */
    public get saveDisablePredicate(): boolean {
        return false;
    }

    public onSave(): void {
        if (this.saveDisablePredicate) {
            return;
        }

        this.closeTransaction(true);
        this.saveEvent.emit();
    }

    public onReset(): void {
        this.closeTransaction(false);
        this.cancelEvent.emit();
    }

    private closeTransaction(commit: boolean): void {
        if (!this.isTransactionOpen) {
            return;
        }

        this.isTransactionOpen = false;

        if (commit) {
            this.form().commitTransaction();
        } else {
            this.form().rollbackTransaction();
        }
    }
}
